import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { arrayUnion, doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { Form, Button, Modal, Row, Col } from 'react-bootstrap';
import {
  FaBook,
  FaBriefcase,
  FaCalendarAlt,
  FaClock,
  FaEnvelope,
  FaGlobeAmericas,
  FaGraduationCap,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPhone,
  FaUser
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../theme/alumno-form.scss';
import { AppContext } from '../../Context';

const normalizeStudentStatus = (value = '') => {
  const normalized = value.toString().trim().toLowerCase();
  if (normalized === 'activos') return 'Activo';
  if (normalized.includes('graduado') || normalized.includes('certificado')) return 'Graduado / Certificado';
  if (normalized === 'baja temporalidad') return 'Baja Temporal';
  return value || 'Activo';
};

const AlumnoForm = () => {
  const { id } = useParams();
  const db = getFirestore();

  const [alumno, setAlumno] = useState({});
  const [estatus, setEstatus] = useState('Activo');
  const [comentario, setComentario] = useState('');
  const [estatusGuardado, setEstatusGuardado] = useState('Activo');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useContext(AppContext);
  const canEditLevel = currentUser?.isMaster === true;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const alumnoRef = doc(db, "users", id);
        const alumnoSnap = await getDoc(alumnoRef);
        if (alumnoSnap.exists()) {
          const normalizedStatus = normalizeStudentStatus(alumnoSnap.data().estatus);
          setAlumno(alumnoSnap.data());
          setEstatus(normalizedStatus);
          setEstatusGuardado(normalizedStatus);
          setComentario(alumnoSnap.data().comentarioEstatus || '');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la información del alumno',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id, db]);

  const handleChange = (e, field) => {
    setAlumno({ ...alumno, [field]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'users', id);
      const updatedData = {
        ...alumno,
        estatus,
        comentarioEstatus: comentario,
        lastUpdatedAt: new Date()
      };

      if (estatus !== estatusGuardado) {
        updatedData.historialEstatus = arrayUnion({
          estatusAnterior: estatusGuardado,
          estatusNuevo: estatus,
          comentario: comentario.trim(),
          fecha: new Date().toISOString(),
          actualizadoPor: currentUser?.uid || currentUser?.id || '',
        });
      }

      await updateDoc(docRef, updatedData);
      setEstatusGuardado(estatus);
      Swal.fire({
        title: 'Guardado',
        text: 'Los cambios han sido guardados con éxito.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'Ocurrió un error al intentar guardar los cambios.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    await handleSave();
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="alumno-form">
        <div className="alumno-form__header">
          <h2>Cargando información...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="alumno-form">
      <div className="alumno-form__header">
        <h2>Detalle del Alumno</h2>
      </div>

      <div className="alumno-form__profile">
        <img
          src={alumno.profilePicture || "/img/default-profile.jpg"}
          alt="Perfil"
          className="alumno-form__profile-image"
        />
        <div className="alumno-form__profile-info">
          <h3>{alumno.name || 'Nombre del Alumno'}</h3>
          <p><FaEnvelope className="me-2" />{alumno.email || 'Email no disponible'}</p>
          <p><FaPhone className="me-2" />{alumno.phone || 'Teléfono no disponible'}</p>
        </div>
      </div>

      <Form>
        <div className="alumno-form__section">
          <h4 className="alumno-form__section-title">Información Personal</h4>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaUser className="me-2" />Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={alumno.name || ''}
                  onChange={(e) => handleChange(e, 'name')}
                  placeholder="Ingrese el nombre completo"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaGraduationCap className="me-2" />Matrícula</Form.Label>
                <Form.Control
                  type="text"
                  name="matricula"
                  value={alumno.matricula || ''}
                  onChange={(e) => handleChange(e, 'matricula')}
                  placeholder="Ingrese la matrícula"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaCalendarAlt className="me-2" />Fecha de nacimiento</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={alumno.date || ''}
                  onChange={(e) => handleChange(e, 'date')}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaUser className="me-2" />Edad</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="120"
                  name="age"
                  value={alumno.age || ''}
                  onChange={(e) => handleChange(e, 'age')}
                  placeholder="Ingrese la edad"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="alumno-form__section">
          <h4 className="alumno-form__section-title">Información de Contacto</h4>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaEnvelope className="me-2" />Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={alumno.email || ''}
                  onChange={(e) => handleChange(e, 'email')}
                  placeholder="Ingrese el email"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaPhone className="me-2" />Teléfono</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={alumno.phone || ''}
                  onChange={(e) => handleChange(e, 'phone')}
                  placeholder="Ingrese el teléfono"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaGlobeAmericas className="me-2" />País</Form.Label>
                <Form.Control
                  type="text"
                  name="country"
                  value={alumno.country || ''}
                  onChange={(e) => handleChange(e, 'country')}
                  placeholder="Ingrese el país"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaMapMarkerAlt className="me-2" />Ciudad</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  value={alumno.city || ''}
                  onChange={(e) => handleChange(e, 'city')}
                  placeholder="Ingrese la ciudad"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaClock className="me-2" />Zona horaria</Form.Label>
                <Form.Control
                  type="text"
                  name="zone"
                  value={alumno.zone || ''}
                  onChange={(e) => handleChange(e, 'zone')}
                  placeholder="Ej. America/Bogota"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="alumno-form__section">
          <h4 className="alumno-form__section-title">Información Académica</h4>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaBook className="me-2" />Nivel Actual</Form.Label>
                {canEditLevel ? (
                  <Form.Select
                    name="level"
                    value={alumno.level || ''}
                    onChange={(e) => handleChange(e, 'level')}
                  >
                    <option value="">Seleccione un nivel</option>
                    <option value="1">Nivel 1</option>
                    <option value="2">Nivel 2</option>
                    <option value="3">Nivel 3</option>
                  </Form.Select>
                ) : (
                  <div className="form-control" style={{ background: '#f8f9fa' }} readOnly>
                    {alumno.level ? `Nivel ${alumno.level}` : 'Sin nivel asignado'}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaCalendarAlt className="me-2" />Fecha de Inscripción</Form.Label>
                <Form.Control
                  type="date"
                  name="inscription"
                  value={alumno.inscription || ''}
                  onChange={(e) => handleChange(e, 'inscription')}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaBook className="me-2" />Estrategia</Form.Label>
                <Form.Control
                  type="text"
                  name="estrategia"
                  value={alumno.estrategia || ''}
                  onChange={(e) => handleChange(e, 'estrategia')}
                  placeholder="Ingrese la estrategia asignada"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label><FaBriefcase className="me-2" />Actividad</Form.Label>
                <Form.Control
                  type="text"
                  name="activity"
                  value={alumno.activity || alumno.actividad || ''}
                  onChange={(e) => handleChange(e, 'activity')}
                  placeholder="Ingrese la actividad del alumno"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="alumno-form__section">
          <h4 className="alumno-form__section-title">Datos adicionales</h4>
          <Form.Group>
            <Form.Label><FaInfoCircle className="me-2" />Información complementaria</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="other"
              value={alumno.other || ''}
              onChange={(e) => handleChange(e, 'other')}
              placeholder="Agregue observaciones o información relevante del alumno"
            />
            <Form.Text className="text-muted">
              Registre aquí niveles cursados, antecedentes e instrucciones de seguimiento para conservar el histórico operativo.
            </Form.Text>
          </Form.Group>
        </div>

        <div className="alumno-form__status">
          <div className="alumno-form__status-current">
            Estatus Actual: <strong>{estatus}</strong>
          </div>
          <div className="alumno-form__status-comment">
            Comentario: {comentario}
          </div>
        </div>

        <div className="alumno-form__actions">
          <Button 
            variant="primary" 
            onClick={() => setShowModal(true)}
            disabled={loading}
          >
            Cambiar Estatus
          </Button>
          <Button 
            variant="success" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </Form>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Estatus del Alumno</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Estatus</Form.Label>
            <Form.Control 
              as="select" 
              value={estatus} 
              onChange={(e) => setEstatus(e.target.value)}
            >
              <option>Activo</option>
              <option>Inactivo</option>
              <option>Baja Temporal</option>
              <option>Baja Definitiva</option>
              <option>Graduado / Certificado</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Comentario</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Ingrese un comentario sobre el cambio de estatus"
            />
            <Form.Text className="text-muted">
              El cambio, comentario, fecha y responsable quedarán registrados en el historial interno.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cerrar</Button>
          <Button 
            variant="primary" 
            onClick={handleSaveStatus}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AlumnoForm;

